/**
 * Fabriques de clés TanStack Query — centralisées pour invalidation cohérente.
 *
 * Chaque domaine expose une racine (`qk.profile.all`) et des clés paramétrées. Les mutations
 * invalident par racine (`queryClient.invalidateQueries({ queryKey: qk.profile.all })`).
 * Les agents Pages n'ont pas besoin d'y toucher : les hooks les utilisent en interne.
 */
export const qk = {
  auth: {
    me: ["auth", "me"] as const,
    prefs: ["auth", "prefs"] as const,
  },
  client: {
    capabilities: ["client", "capabilities"] as const,
  },
  profile: {
    all: ["profile"] as const,
    client: () => ["profile", "client"] as const,
    public: (name: string) => ["profile", "public", name] as const,
    publicGear: (name: string) => ["profile", "public", "gear", name] as const,
    stats: () => ["profile", "client", "stats"] as const,
    economy: () => ["profile", "client", "economy"] as const,
    history: () => ["profile", "client", "history"] as const,
    claims: () => ["profile", "client", "claims"] as const,
    homes: () => ["profile", "client", "homes"] as const,
    quests: () => ["profile", "client", "quests"] as const,
    battlePass: () => ["profile", "client", "battlepass"] as const,
    publicBattlePass: (name: string) => ["profile", "public", "battlepass", name] as const,
    tokens: () => ["profile", "client", "tokens"] as const,
    jobs: () => ["profile", "client", "jobs"] as const,
    levels: () => ["profile", "client", "levels"] as const,
    playerClass: () => ["profile", "client", "class"] as const,
  },
  inventory: {
    all: ["inventory"] as const,
    client: () => ["inventory", "client"] as const,
    gear: () => ["inventory", "client", "gear"] as const,
  },
  players: {
    all: ["players"] as const,
    search: (q: string, limit: number) => ["players", "search", q, limit] as const,
    recent: (limit: number) => ["players", "recent", limit] as const,
  },
  guild: {
    all: ["guild"] as const,
    client: () => ["guild", "client"] as const,
    publicList: () => ["guild", "public", "list"] as const,
    public: (name: string) => ["guild", "public", name] as const,
  },
  market: {
    all: ["market"] as const,
    browse: (query: unknown) => ["market", "browse", query] as const,
    mine: () => ["market", "mine"] as const,
  },
  leaderboard: {
    all: ["leaderboard"] as const,
    metric: (metric: string, limit: number, offset: number) =>
      ["leaderboard", metric, limit, offset] as const,
    dungeon: (query: unknown) => ["leaderboard", "dungeon", query] as const,
  },
  server: {
    online: ["server", "online"] as const,
    status: ["server", "status"] as const,
    conscienceWorld: ["server", "conscience", "world"] as const,
    conscienceChronicles: (limit: number) => ["server", "conscience", "chronicles", limit] as const,
    conscienceLive: (limit: number) => ["server", "conscience", "live", limit] as const,
  },
  wiki: {
    all: ["wiki"] as const,
    index: () => ["wiki", "index"] as const,
    category: (category: string) => ["wiki", "category", category] as const,
    entry: (category: string, id: string) => ["wiki", "entry", category, id] as const,
    ranks: () => ["wiki", "ranks"] as const,
    obtentionIndex: () => ["wiki", "obtention", "index"] as const,
    obtention: (id: string) => ["wiki", "obtention", id] as const,
  },
  admin: {
    all: ["admin"] as const,
    players: (query: unknown) => ["admin", "players", query] as const,
    player: (uuid: string) => ["admin", "player", uuid] as const,
    playerInventory: (uuid: string) => ["admin", "player", uuid, "inventory"] as const,
    perf: () => ["admin", "perf"] as const,
    audit: (query: unknown) => ["admin", "audit", query] as const,
    masterAudit: (query: unknown) => ["admin", "master", "audit", query] as const,
    config: (plugin: string, key: string) => ["admin", "config", plugin, key] as const,
    plugins: () => ["admin", "plugins"] as const,
    pluginFiles: (plugin: string) => ["admin", "plugins", plugin] as const,
    configFile: (plugin: string, file: string) =>
      ["admin", "config", "file", plugin, file] as const,
    deployPlugins: () => ["admin", "deploy", "plugins"] as const,
    wipePreview: () => ["admin", "wipe", "preview"] as const,
    economyWipePreview: () => ["admin", "economy", "wipe", "preview"] as const,
    battlePassPlayers: (query: unknown) => ["admin", "battlepass", "players", query] as const,
    npcs: () => ["admin", "npcs"] as const,
    dungeonSchematics: () => ["admin", "dungeons", "schematics"] as const,
    shopCatalog: () => ["admin", "shop", "catalog"] as const,
    shopStripe: () => ["admin", "shop", "stripe"] as const,
  },
  content: {
    all: ["content"] as const,
    publicList: (type?: string) => ["content", "public", type ?? "all"] as const,
    publicEntry: (key: string) => ["content", "public", "entry", key] as const,
    adminList: (query: unknown) => ["content", "admin", query] as const,
    adminEntry: (key: string) => ["content", "admin", "entry", key] as const,
  },
  settings: {
    all: ["settings"] as const,
    public: () => ["settings", "public"] as const,
  },
  shop: {
    all: ["shop"] as const,
    catalog: () => ["shop", "catalog"] as const,
    serverLive: () => ["shop", "server", "live"] as const,
  },
  community: {
    all: ["community"] as const,
    stats: () => ["community", "stats"] as const,
    forum: {
      all: ["community", "forum"] as const,
      categories: () => ["community", "forum", "categories"] as const,
      threads: (categoryId: string, offset: number) =>
        ["community", "forum", "threads", categoryId, offset] as const,
      thread: (id: number, offset: number) => ["community", "forum", "thread", id, offset] as const,
      adminThreads: (query: unknown) => ["community", "forum", "admin", "threads", query] as const,
      adminPosts: (query: unknown) => ["community", "forum", "admin", "posts", query] as const,
    },
    reports: {
      all: ["community", "reports"] as const,
      mine: (offset: number) => ["community", "reports", "mine", offset] as const,
      mineDetail: (id: number) => ["community", "reports", "mine", "detail", id] as const,
      adminList: (query: unknown) => ["community", "reports", "admin", query] as const,
      adminDetail: (id: number) => ["community", "reports", "admin", "detail", id] as const,
      adminStats: () => ["community", "reports", "admin", "stats"] as const,
    },
    suggestions: {
      all: ["community", "suggestions"] as const,
      list: (query: unknown) => ["community", "suggestions", "list", query] as const,
      detail: (id: number) => ["community", "suggestions", "detail", id] as const,
      adminList: (query: unknown) => ["community", "suggestions", "admin", query] as const,
      adminDetail: (id: number) => ["community", "suggestions", "admin", "detail", id] as const,
    },
  },
  vote: {
    all: ["vote"] as const,
    sites: () => ["vote", "sites"] as const,
    status: () => ["vote", "status"] as const,
    top: (limit: number) => ["vote", "top", limit] as const,
  },
  install: {
    status: ["install", "status"] as const,
  },
} as const;
